/**
 * Tema i jezik: lokalni izbor (`sm-theme`, `sm-lang`) je izvor istine za prikaz, a promjena se
 * dodatno čuva na korisniku (PATCH /me) kad je korisnik prijavljen.
 */
import type { LangCode } from '@sm/i18n';
import { LANGS } from '@sm/i18n';

import { applyHtmlLang, i18n, readStoredLang, storeLang } from './i18n';

export type ThemeName = 'light' | 'dark';
export const THEME_STORAGE_KEY = 'sm-theme';

export function readStoredTheme(): ThemeName | null {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

export function currentTheme(): ThemeName {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function applyTheme(theme: ThemeName, persist = true): void {
  document.documentElement.setAttribute('data-theme', theme);
  if (persist) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignoriše se */
    }
  }
}

export function isLangReady(code: LangCode): boolean {
  return LANGS.find((l) => l.code === code)?.ready ?? false;
}

/** Mijenja jezik UI-ja; jezici koji nisu spremni (de) se odbijaju. Vraća da li je promijenjeno. */
export function applyLang(code: LangCode, persist = true): boolean {
  if (!isLangReady(code)) return false;
  void i18n.changeLanguage(code);
  applyHtmlLang(code);
  if (persist) storeLang(code);
  return true;
}

/**
 * Nakon učitavanja /me: ako lokalno nema izbora, preuzima se izbor sa naloga.
 */
export function syncFromServer(server: { theme?: string; language?: string }): void {
  if (!readStoredTheme() && (server.theme === 'dark' || server.theme === 'light')) {
    applyTheme(server.theme, false);
  }
  if (!readStoredLang() && (server.language === 'sr' || server.language === 'en')) {
    applyLang(server.language, false);
  }
}
